/**
 * Elevation schematics — entry point that wires every tier into the
 * shared registry. Importing this module from `RenderWallElevation`
 * is what causes the registry to populate, so any consumer that
 * needs `getSchematicComponent` must import this index (not the
 * raw registry).
 *
 * v1 — doors + windows.
 * v2 — kitchen appliances (range, wall-oven, dishwasher, refrigerator).
 * v3 — bath appliances + faucets (registered via `./v3-registrations`).
 * Subsequent tiers will add to this file as they ship — each tier
 * keeps its own `vN-registrations.js` to minimize merge conflicts
 * between parallel agents.
 */
import {
  registerSchematic,
  getSchematicComponent,
  _getRegisteredTypes,
} from './registry';
import DoorElevationSchematic from './DoorElevationSchematic';
import WindowElevationSchematic from './WindowElevationSchematic';
import { RangeSchematic } from './RangeSchematic';
import { WallOvenSchematic } from './WallOvenSchematic';
import { DishwasherSchematic } from './DishwasherSchematic';
import { RefrigeratorSchematic } from './RefrigeratorSchematic';
import { VentHoodSchematic } from './VentHoodSchematic';
import './v4a-registrations';
import './v4b-registrations';
import * as v3 from './v3-registrations';
import { registerV5Schematics } from './v5-registrations';
import { ChairSchematic } from './ChairSchematic';
import { TableSchematic } from './TableSchematic';
import { SofaSchematic } from './SofaSchematic';
import { BedSchematic } from './BedSchematic';
import { TelevisionSchematic } from './TelevisionSchematic';
import { CooktopSchematic } from './CooktopSchematic';
import { WoodStoveSchematic } from './WoodStoveSchematic';
import { StorageSchematic } from './StorageSchematic';
import { CeilingLightSchematic } from './CeilingLightSchematic';
import { BarStoolSchematic } from './BarStoolSchematic';
import {
  DormBedLowSchematic,
  DormBedStandardSchematic,
  DormBedCaptainSchematic,
  DormLoftSchematic,
  DormBunkLowSchematic,
  DormBunkStandardSchematic,
  DormBunkCaptainSchematic,
} from './DormBedSchematic';
import { DormDresserSchematic } from './DormDresserSchematic';
import { DormDeskSchematic } from './DormDeskSchematic';
import { DormWardrobeSchematic } from './DormWardrobeSchematic';
import { DormChairSchematic } from './DormChairSchematic';
import { DormStackableDrawersSchematic } from './DormStackableDrawersSchematic';
import { DormRollingCartSchematic } from './DormRollingCartSchematic';
import {
  DormMiniFridgeCompactSchematic,
  DormMiniFridgeMidsizeSchematic,
} from './DormMiniFridgeSchematic';
import {
  DormCorkboardSchematic,
  DormWhiteboardSchematic,
  DormPosterSchematic,
  DormTapestrySchematic,
} from './DormBoardSchematic';
import { MirrorSchematic } from './MirrorSchematic';
import { DormFloatingShelfSchematic } from './DormFloatingShelfSchematic';
import {
  DormWallOarSchematic,
  DormRattanFanSchematic,
} from './DormCoastalSchematic';
import {
  DormVinylWallSchematic,
  DormNeonSignSchematic,
} from './DormRetroSchematic';
import { DormPennantBannerSchematic } from './DormPennantBannerSchematic';
import { DormFloorLampSchematic } from './DormFloorLampSchematic';
import { DormMonitorSchematic } from './DormMonitorSchematic';
import {
  DormOverDoorHooksSchematic,
  DormOverDoorMirrorSchematic,
} from './DormOverDoorSchematic';
import { DormDeskHutchSchematic } from './DormDeskHutchSchematic';
import { DormMicrowaveSchematic } from './DormMicrowaveSchematic';
import { DormUnitVentilatorSchematic } from './DormUnitVentilatorSchematic';
import { DormStorageBinSchematic } from './DormStorageBinSchematic';
import {
  DormStickVacuumSchematic,
  DormHandheldVacuumSchematic,
} from './DormVacuumSchematic';
import { DormShoeRackSchematic } from './DormShoeRackSchematic';
import {
  DormAirPurifierTowerSchematic,
  DormAirPurifierCompactSchematic,
} from './DormAirPurifierSchematic';
import { CurtainRodSchematic } from './CurtainRodSchematic';
import { RadiatorSchematic } from './RadiatorSchematic';
import { BaseboardHeaterSchematic } from './BaseboardHeaterSchematic';
import { StorageOttomanSchematic } from './StorageOttomanSchematic';
import { DormLaundryHamperSchematic } from './DormLaundryHamperSchematic';
import { DeskLampSchematic } from './DeskLampSchematic';
import { DormSurgeProtectorSchematic } from './DormSurgeProtectorSchematic';
import { DormBookshelfSchematic } from './DormBookshelfSchematic';
import { DormWastebasketSchematic } from './DormWastebasketSchematic';
import { DormBeanbagSchematic } from './DormBeanbagSchematic';
import {
  DormTowerFanSchematic,
  DormDeskFanSchematic,
} from './DormFanSchematic';
import { DormPrinterSchematic } from './DormPrinterSchematic';
import {
  DormDeskPlantSchematic,
  DormFloorPlantSchematic,
} from './DormPlantSchematic';
import { DormRugSchematic } from './DormRugSchematic';
import {
  DormFairyLightsSchematic,
  DormFairyLightsCurtainSchematic,
} from './DormFairyLightsSchematic';
import { DormCommandHookSchematic } from './DormCommandHookSchematic';

registerSchematic('door', DoorElevationSchematic);
registerSchematic('window', WindowElevationSchematic);
registerSchematic('range', RangeSchematic);
registerSchematic('wall-oven', WallOvenSchematic);
registerSchematic('dishwasher', DishwasherSchematic);
registerSchematic('refrigerator', RefrigeratorSchematic);
registerV5Schematics();

// New procedural types (chairs/tables/sofas/beds/tv/storage/lights).
registerSchematic('chair', ChairSchematic);
registerSchematic('table', TableSchematic);
registerSchematic('sofa', SofaSchematic);
registerSchematic('bed', BedSchematic);
registerSchematic('television', TelevisionSchematic);
registerSchematic('cooktop', CooktopSchematic);
registerSchematic('wood-stove', WoodStoveSchematic);
registerSchematic('storage', StorageSchematic);
registerSchematic('ceiling-light', CeilingLightSchematic);
registerSchematic('bar-stool', BarStoolSchematic);

// Dorm beds — single, loft, bunk at various rail heights.
registerSchematic('dorm-bed-low-xl', DormBedLowSchematic);
registerSchematic('dorm-bed-low-twin', DormBedLowSchematic);
registerSchematic('dorm-bed-standard-xl', DormBedStandardSchematic);
registerSchematic('dorm-bed-standard-twin', DormBedStandardSchematic);
registerSchematic('dorm-bed-captain-xl', DormBedCaptainSchematic);
registerSchematic('dorm-bed-captain-twin', DormBedCaptainSchematic);
registerSchematic('dorm-bed-loft-xl', DormLoftSchematic);
registerSchematic('dorm-bed-loft-twin', DormLoftSchematic);
registerSchematic('dorm-bunk-standard-xl', DormBunkStandardSchematic);
registerSchematic('dorm-bunk-standard-twin', DormBunkStandardSchematic);
registerSchematic('dorm-bunk-low-xl', DormBunkLowSchematic);
registerSchematic('dorm-bunk-low-twin', DormBunkLowSchematic);
registerSchematic('dorm-bunk-captain-xl', DormBunkCaptainSchematic);
registerSchematic('dorm-bunk-captain-twin', DormBunkCaptainSchematic);

// Dorm furniture
registerSchematic('dorm-dresser', DormDresserSchematic);
registerSchematic('dorm-desk', DormDeskSchematic);
registerSchematic('dorm-wardrobe', DormWardrobeSchematic);
registerSchematic('dorm-chair', DormChairSchematic);
registerSchematic('dorm-stackable-drawers', DormStackableDrawersSchematic);
registerSchematic('dorm-rolling-cart', DormRollingCartSchematic);
registerSchematic('dorm-minifridge-compact', DormMiniFridgeCompactSchematic);
registerSchematic('dorm-minifridge-midsize', DormMiniFridgeMidsizeSchematic);
registerSchematic('dorm-desk-hutch', DormDeskHutchSchematic);
registerSchematic('dorm-microwave', DormMicrowaveSchematic);
// the microwave in a run's microwave cabinet: the same countertop microwave
registerSchematic('microwave', DormMicrowaveSchematic);
registerSchematic('dorm-corkboard', DormCorkboardSchematic);
// Theme wall art (coastal / 80s retro / cabincore). Every WALL_ART_DIMS key
// must resolve to a schematic — see dormDecorRegistrations.test.jsx.
registerSchematic('dorm-wall-oar', DormWallOarSchematic);
registerSchematic('dorm-rattan-fan', DormRattanFanSchematic);
registerSchematic('dorm-vinyl-wall', DormVinylWallSchematic);
registerSchematic('dorm-neon-sign', DormNeonSignSchematic);
registerSchematic('dorm-pennant-banner', DormPennantBannerSchematic);
registerSchematic('dorm-whiteboard', DormWhiteboardSchematic);
registerSchematic('dorm-unit-ventilator', DormUnitVentilatorSchematic);
registerSchematic('dorm-storage-bin', DormStorageBinSchematic);
registerSchematic('dorm-stick-vacuum', DormStickVacuumSchematic);
registerSchematic('dorm-handheld-vacuum', DormHandheldVacuumSchematic);
registerSchematic('dorm-shoe-rack', DormShoeRackSchematic);
registerSchematic('dorm-air-purifier-tower', DormAirPurifierTowerSchematic);
registerSchematic('dorm-air-purifier-compact', DormAirPurifierCompactSchematic);
registerSchematic('curtain-rod', CurtainRodSchematic);
registerSchematic('radiator', RadiatorSchematic);
registerSchematic('baseboard-heater', BaseboardHeaterSchematic);
registerSchematic('storage-ottoman', StorageOttomanSchematic);
registerSchematic('dorm-laundry-hamper', DormLaundryHamperSchematic);
registerSchematic('desk-lamp', DeskLampSchematic);
registerSchematic('dorm-surge-protector', DormSurgeProtectorSchematic);
registerSchematic('dorm-bookshelf', DormBookshelfSchematic);
registerSchematic('dorm-bookshelf-wide', DormBookshelfSchematic);
registerSchematic('dorm-wastebasket', DormWastebasketSchematic);
registerSchematic('dorm-beanbag', DormBeanbagSchematic);
registerSchematic('dorm-tower-fan', DormTowerFanSchematic);
registerSchematic('dorm-desk-fan', DormDeskFanSchematic);
registerSchematic('dorm-printer', DormPrinterSchematic);
registerSchematic('dorm-desk-plant', DormDeskPlantSchematic);
registerSchematic('dorm-floor-plant', DormFloorPlantSchematic);
registerSchematic('dorm-rug', DormRugSchematic);
registerSchematic('dorm-rug-circular', DormRugSchematic);
registerSchematic('dorm-fairy-lights', DormFairyLightsSchematic);
registerSchematic('dorm-fairy-lights-curtain', DormFairyLightsCurtainSchematic);
registerSchematic('dorm-command-hook', DormCommandHookSchematic);

// Aliases — `stove` / `oven` / `range-hood` are alternate type keys used
// by some registries that should render the same as the canonical
// appliance schematics.
registerSchematic('stove', RangeSchematic);
registerSchematic('oven', WallOvenSchematic);
registerSchematic('range-hood', VentHoodSchematic);

// Aliases for normalized persisted dorm keys (dorm item normalization).
// Reuse the existing schematic for the base/singular form; keys with no
// schematic fall back to a rectangle, which is acceptable.
registerSchematic('dorm-tv', TelevisionSchematic);
registerSchematic('dorm-desk-lamp', DeskLampSchematic);
registerSchematic('dorm-command-hooks', DormCommandHookSchematic);

// Dorm decor and long-tail items that had NO schematic and so fell through to
// the plain-rectangle fallback above. That fallback is fine for a box-shaped
// object, but these are the pieces whose SHAPE is the information: a mirror, a
// poster and a tapestry are wall decor, and a wall elevation exists precisely
// to show them — as rectangles they were indistinguishable from each other and
// from the wall. The lamp/monitor/over-door items are worse than uninformative
// as boxes: their bounding box is a tall column that reads as furniture.
//
// Every key here has a procedural 3D renderer, so each of these pairs a
// schematic with an existing renderer (web-app/CLAUDE.md, "drawn TWICE").
// NOTE: an object carrying a modelId/modelUrl never reaches this registry —
// RenderWallElevation.js resolves the GLB edge-projection path first.
registerSchematic('dorm-mirror', MirrorSchematic);
registerSchematic('dorm-poster', DormPosterSchematic);
registerSchematic('dorm-tapestry', DormTapestrySchematic);
registerSchematic('dorm-floating-shelf', DormFloatingShelfSchematic);
registerSchematic('dorm-floor-lamp', DormFloorLampSchematic);
registerSchematic('dorm-monitor', DormMonitorSchematic);
registerSchematic('dorm-over-door-hooks', DormOverDoorHooksSchematic);
registerSchematic('dorm-over-door-mirror', DormOverDoorMirrorSchematic);
// dorm-trash-can and dorm-wastebasket are size variants (12x9x20 vs 10x8x18)
// drawn by the same dormWastebasketRenderer, so they share its schematic too.
registerSchematic('dorm-trash-can', DormWastebasketSchematic);

// Deliberately NOT registered — these keys stay on the rectangle fallback.
//
// The five legacy keys (dorm-mini-fridge, dorm-laundry-bin, dorm-3-tier-cart,
// dorm-fan, dorm-air-purifier) are orphans kept only so already-persisted
// rooms resolve.
//
// dorm-mini-fridge WAS the ambiguous one — the 3D map routed it to the COMPACT
// renderer while its dims matched MIDSIZE. That was resolved on 2026-08-24 by
// re-pointing the 3D alias to the midsize renderer, so registering
// DormMiniFridgeMidsizeSchematic here is now consistent and safe. Not done
// yet only because it is a separate decision from the de-alias work.
//
// dorm-3-tier-cart carries its own unresolved defect: its ITEM_DIMS_INCHES
// entry is 14w x 18d while its twin dorm-rolling-cart is 18w x 14d — width and
// depth look transposed — so it would draw at the wrong aspect either way.
//
// `dorm-desk-chair` is out for the same reason, found by rendering it: its
// ITEM_DIMS_INCHES entry is 20x20x32 (chair-sized — a desk is 42x24x30) but
// the 3D renderer map routes it to dormDeskRenderer, so it draws as a desk
// squeezed into a chair's footprint. Registering DormDeskSchematic would
// canonise the desk reading; registering DormChairSchematic would canonise
// the dimensions. That contradiction wants resolving at the source first.
// (In practice dorm-layout-generation.js expands the iOS desk+chair combo
// into two separate objects, so a persisted bare dorm-desk-chair is rare.)

export {
  registerSchematic,
  getSchematicComponent,
  _getRegisteredTypes,
  DoorElevationSchematic,
  WindowElevationSchematic,
  RangeSchematic,
  WallOvenSchematic,
  DishwasherSchematic,
  RefrigeratorSchematic,
  ChairSchematic,
  TableSchematic,
  SofaSchematic,
  BedSchematic,
  TelevisionSchematic,
  CooktopSchematic,
  WoodStoveSchematic,
  StorageSchematic,
  CeilingLightSchematic,
  BarStoolSchematic,
  DormBedLowSchematic,
  DormBedStandardSchematic,
  DormBedCaptainSchematic,
  DormLoftSchematic,
  DormBunkLowSchematic,
  DormBunkStandardSchematic,
  DormBunkCaptainSchematic,
  DormDresserSchematic,
  DormDeskSchematic,
  DormWardrobeSchematic,
  DormChairSchematic,
  DormStackableDrawersSchematic,
  DormRollingCartSchematic,
  DormMiniFridgeCompactSchematic,
  DormMiniFridgeMidsizeSchematic,
  DormDeskHutchSchematic,
  DormMicrowaveSchematic,
  DormCorkboardSchematic,
  DormWhiteboardSchematic,
  DormUnitVentilatorSchematic,
  DormStorageBinSchematic,
  DormStickVacuumSchematic,
  DormHandheldVacuumSchematic,
  DormShoeRackSchematic,
  DormAirPurifierTowerSchematic,
  DormAirPurifierCompactSchematic,
  CurtainRodSchematic,
  RadiatorSchematic,
  BaseboardHeaterSchematic,
  StorageOttomanSchematic,
  DormLaundryHamperSchematic,
  DeskLampSchematic,
  DormSurgeProtectorSchematic,
  DormBookshelfSchematic,
  DormWastebasketSchematic,
  DormBeanbagSchematic,
  DormTowerFanSchematic,
  DormDeskFanSchematic,
  DormPrinterSchematic,
  DormDeskPlantSchematic,
  DormFloorPlantSchematic,
  DormRugSchematic,
  DormFairyLightsSchematic,
  DormFairyLightsCurtainSchematic,
  DormCommandHookSchematic,
  MirrorSchematic,
  DormPosterSchematic,
  DormTapestrySchematic,
  DormFloatingShelfSchematic,
  DormFloorLampSchematic,
  DormMonitorSchematic,
  DormOverDoorHooksSchematic,
  DormOverDoorMirrorSchematic,
};

export const {
  ToiletSchematic,
  BathtubSchematic,
  KitchenSinkSchematic,
  SinkSchematic,
  KitchenFaucetSchematic,
  BathroomSinkFaucetSchematic,
  BathroomTubFaucetSchematic,
  ShowerheadSchematic,
  ShowerControlsSchematic,
} = v3;
